import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Tile area calculator — converts dimensions + wastage into cart quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} widthInput
 * @property {HTMLInputElement} lengthInput
 * @property {HTMLElement} wastageValue
 * @property {HTMLElement} unitsValue
 * @property {HTMLElement} areaSummary
 * @property {HTMLElement} measuredAreaLine
 * @property {HTMLElement} wastageAreaLine
 * @property {HTMLElement} areaPriceLine
 * @property {HTMLElement} unitsHeading
 * @property {HTMLElement} unitPrice
 * @property {HTMLElement} totalValue
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} addToCartLabel
 * @property {HTMLButtonElement} unitMButton
 * @property {HTMLButtonElement} unitCmButton
 * @property {HTMLButtonElement} decreaseWastageButton
 * @property {HTMLButtonElement} increaseWastageButton
 * @property {HTMLButtonElement} decreaseUnitsButton
 * @property {HTMLButtonElement} increaseUnitsButton
 * @property {HTMLElement} [wastageTooltip]
 * @property {HTMLButtonElement} [wastageTooltipTrigger]
 *
 * @extends {Component<Refs>}
 */
class TileCalculatorComponent extends Component {
  requiredRefs = [
    'widthInput',
    'lengthInput',
    'wastageValue',
    'unitsValue',
    'areaSummary',
    'measuredAreaLine',
    'wastageAreaLine',
    'areaPriceLine',
    'unitsHeading',
    'unitPrice',
    'totalValue',
    'addToCartButton',
    'addToCartLabel',
    'unitMButton',
    'unitCmButton',
    'decreaseWastageButton',
    'increaseWastageButton',
    'decreaseUnitsButton',
    'increaseUnitsButton',
  ];

  /** @type {'m' | 'cm'} */
  #measureUnit = 'm';

  /** @type {number} */
  #wastage = 10;

  /** @type {boolean} */
  #manualUnits = false;

  /** @type {number | null} */
  #manualUnitsValue = null;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #controlAbortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();

    this.#measureUnit = this.dataset.measureUnit === 'cm' ? 'cm' : 'm';
    this.#wastage = parseInt(this.dataset.defaultWastage || '10', 10) || 0;

    this.#bindControls();

    const productForm = this.closest('product-form-component');
    productForm?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });

    const availability = this.closest('product-availability-component');
    availability?.addEventListener('beaumont:purchase-mode-change', this.#onPurchaseModeChange, {
      signal: this.#abortController.signal,
    });

    this.#updateMeasureToggle();
    this.#updateWastageDisplay();
    this.#recalculate();
    this.#syncAddToCartState();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#controlAbortController?.abort();
  }

  #bindControls() {
    this.#controlAbortController?.abort();
    this.#controlAbortController = new AbortController();
    const signal = this.#controlAbortController.signal;

    const bindClick = (/** @type {keyof Refs} */ refName, /** @type {(event: Event) => void} */ handler) => {
      const element = this.refs[refName];
      if (!(element instanceof HTMLElement)) return;
      element.addEventListener('click', (event) => handler.call(this, event), { signal });
    };

    bindClick('unitMButton', this.setMeasureUnit);
    bindClick('unitCmButton', this.setMeasureUnit);
    bindClick('decreaseWastageButton', this.decreaseWastage);
    bindClick('increaseWastageButton', this.increaseWastage);
    bindClick('decreaseUnitsButton', this.decreaseUnits);
    bindClick('increaseUnitsButton', this.increaseUnits);
    bindClick('addToCartButton', this.submitToCart);

    const { wastageTooltip, wastageTooltipTrigger } = this.refs;
    if (wastageTooltip instanceof HTMLElement && wastageTooltipTrigger instanceof HTMLButtonElement) {
      wastageTooltipTrigger.addEventListener('click', (event) => this.#toggleWastageTooltip(event), { signal });
      document.addEventListener('click', (event) => this.#closeWastageTooltip(event), { signal });
    }

    const { widthInput, lengthInput, unitsValue } = this.refs;
    widthInput?.addEventListener('input', (event) => this.onDimensionInput(event), { signal });
    lengthInput?.addEventListener('input', (event) => this.onDimensionInput(event), { signal });
    unitsValue?.addEventListener('change', (event) => this.onUnitsInput(event), { signal });
  }

  #updateWastageDisplay() {
    this.refs.wastageValue.textContent = `${this.#wastage}%`;
  }

  /**
   * @param {Event} event
   */
  #toggleWastageTooltip(event) {
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const { wastageTooltip, wastageTooltipTrigger } = this.refs;
    if (!(wastageTooltip instanceof HTMLElement) || !(wastageTooltipTrigger instanceof HTMLButtonElement)) {
      return;
    }

    const isOpen = wastageTooltip.dataset.open === 'true';
    wastageTooltip.dataset.open = isOpen ? 'false' : 'true';
    wastageTooltipTrigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
  }

  /**
   * @param {Event} event
   */
  #closeWastageTooltip(event) {
    const { wastageTooltip, wastageTooltipTrigger } = this.refs;
    if (!(wastageTooltip instanceof HTMLElement) || !(wastageTooltipTrigger instanceof HTMLButtonElement)) {
      return;
    }

    const target = event.target;
    if (target instanceof Node && wastageTooltip.contains(target)) {
      return;
    }

    wastageTooltip.dataset.open = 'false';
    wastageTooltipTrigger.setAttribute('aria-expanded', 'false');
  }

  /**
   * @param {Event} event
   */
  setMeasureUnit(event) {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;

    const unit = target.dataset.unit;
    if (unit !== 'm' && unit !== 'cm') return;
    if (unit === this.#measureUnit) return;

    this.#convertDimensionInputs(this.#measureUnit, unit);
    this.#measureUnit = unit;
    this.dataset.measureUnit = unit;
    this.#manualUnits = false;
    this.#updateMeasureToggle();
    this.#recalculate();
  }

  /**
   * @param {'m' | 'cm'} fromUnit
   * @param {'m' | 'cm'} toUnit
   */
  #convertDimensionInputs(fromUnit, toUnit) {
    const factor = fromUnit === 'm' && toUnit === 'cm' ? 100 : fromUnit === 'cm' && toUnit === 'm' ? 0.01 : 1;
    if (factor === 1) return;

    for (const input of [this.refs.widthInput, this.refs.lengthInput]) {
      const value = parseFloat(input.value);
      if (!value || value <= 0) continue;

      input.value = this.#formatDimensionValue(value * factor, toUnit);
    }
  }

  /**
   * @param {number} value
   * @param {'m' | 'cm'} unit
   */
  #formatDimensionValue(value, unit) {
    const rounded = Math.round(value * 10000) / 10000;

    if (unit === 'cm') {
      const cm = Math.round(rounded * 100) / 100;
      return Number.isInteger(cm) ? String(Math.round(cm)) : String(cm);
    }

    const metres = Math.round(rounded * 1000) / 1000;
    return String(parseFloat(metres.toFixed(3)));
  }

  /** @param {Event} event */
  onDimensionInput(event) {
    event.preventDefault();
    this.#manualUnits = false;
    this.#recalculate();
  }

  increaseWastage(event) {
    event.preventDefault();
    this.#wastage = Math.min(50, this.#wastage + 1);
    this.#manualUnits = false;
    this.#updateWastageDisplay();
    this.#recalculate();
  }

  decreaseWastage(event) {
    event.preventDefault();
    this.#wastage = Math.max(0, this.#wastage - 1);
    this.#manualUnits = false;
    this.#updateWastageDisplay();
    this.#recalculate();
  }

  increaseUnits(event) {
    event.preventDefault();
    const quantitySelector = this.#getQuantitySelector();
    const current = this.#getDisplayedUnits();
    let next = current + 1;

    if (quantitySelector?.getCurrentValues) {
      const { step } = quantitySelector.getCurrentValues();
      const effectiveMax = quantitySelector.getEffectiveMax?.() ?? null;
      next = current + (step || 1);
      if (effectiveMax !== null) {
        next = Math.min(effectiveMax, next);
      }
    }

    this.#manualUnits = true;
    this.#manualUnitsValue = next;
    this.#applyUnits(next);
  }

  decreaseUnits(event) {
    event.preventDefault();
    const quantitySelector = this.#getQuantitySelector();
    const current = this.#getDisplayedUnits();
    let next = current - 1;

    if (quantitySelector?.getCurrentValues) {
      const { min, step } = quantitySelector.getCurrentValues();
      next = Math.max(min, current - (step || 1));
    } else {
      next = Math.max(1, next);
    }

    this.#manualUnits = true;
    this.#manualUnitsValue = next;
    this.#applyUnits(next);
  }

  /** @param {Event} event */
  onUnitsInput(event) {
    const input = event.currentTarget;
    if (!(input instanceof HTMLInputElement)) return;
  
    const quantitySelector = this.#getQuantitySelector();
    let next = parseInt(input.value, 10);
  
    if (!Number.isFinite(next) || next < 1) {
      next = 1;
    }
  
    if (quantitySelector?.getCurrentValues) {
      const { min, max, step } = quantitySelector.getCurrentValues();
      next = this.#snapToQuantityRule(next, min, max, step);
    }
  
    this.#manualUnits = true;
    this.#manualUnitsValue = next;
    this.#applyUnits(next);
  }

  /**
   * Prefer data-submit-type over type="submit": cart-order-conflict flips the
   * hidden ATC to type="button" while keeping it clickable for messaging.
   * @returns {HTMLButtonElement | null}
   */
  #getHiddenAddToCartButton() {
    const productForm = this.closest('product-form-component');
    const button = productForm?.querySelector(
      'add-to-cart-component [ref="addToCartButton"][data-submit-type="submit"], add-to-cart-component [ref="addToCartButton"]'
    );

    return button instanceof HTMLButtonElement ? button : null;
  }

  submitToCart(event) {
    event.preventDefault();

    this.#getHiddenAddToCartButton()?.click();
  }

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant) return;

    if (typeof variant.price === 'number') {
      this.dataset.variantPrice = String(variant.price);
    }

    this.#manualUnits = false;
    this.#recalculate();
    requestAnimationFrame(() => this.#syncAddToCartState());
  };

  #onPurchaseModeChange = () => {
    this.#syncAddToCartState();
  };

  #syncAddToCartState() {
    const hiddenButton = this.#getHiddenAddToCartButton();
    const { addToCartButton, addToCartLabel } = this.refs;

    if (!hiddenButton || !addToCartButton || !addToCartLabel) return;

    // Conflict mode keeps the ATC clickable (not disabled) so messaging can run.
    addToCartButton.disabled = hiddenButton.disabled;

    const labelText = hiddenButton.querySelector('.add-to-cart-text__content span span')?.textContent?.trim();
    if (labelText) {
      addToCartLabel.textContent = labelText;
    }
  }

  #updateMeasureToggle() {
    const isM = this.#measureUnit === 'm';
    this.refs.unitMButton.setAttribute('aria-pressed', String(isM));
    this.refs.unitCmButton.setAttribute('aria-pressed', String(!isM));
    this.refs.unitMButton.classList.toggle('tile-calculator__unit-toggle--active', isM);
    this.refs.unitCmButton.classList.toggle('tile-calculator__unit-toggle--active', !isM);
    this.#updateDimensionUnitLabels();
  }

  #updateDimensionUnitLabels() {
    const label = this.#measureUnit === 'cm' ? 'CM' : 'M';
    this.querySelectorAll('[data-measure-unit-label]').forEach((element) => {
      element.textContent = label;
    });
  }

  #getDisplayedUnits() {
    return parseInt(this.refs.unitsValue.value || '1', 10) || 1;
  }

  #getConfig() {
    const sqmPerUnit = parseFloat(this.dataset.sqmPerUnit || '0');
    const variantPrice = parseInt(this.dataset.variantPrice || '0', 10);
    const sequenceGroup = (this.dataset.unitSequenceGroup || '').toUpperCase();
    const displayUnit = (this.dataset.displayUnit || '').toLowerCase();
    const salesUnit = (this.dataset.salesUnit || '').toLowerCase();

    return { sqmPerUnit, variantPrice, sequenceGroup, displayUnit, salesUnit };
  }

  /**
   * @param {number} rawUnits
   * @param {string} sequenceGroup
   * @param {string} displayUnit
   * @param {string} salesUnit
   */
  #roundUnits(rawUnits, sequenceGroup, displayUnit, salesUnit) {
    if (sequenceGroup === 'SEQ-ECH' && displayUnit === 'ea' && salesUnit === 'sqm') {
      return Math.max(1, Math.round(rawUnits));
    }

    return Math.max(1, Math.ceil(rawUnits));
  }

  /**
   * @param {number} value
   * @param {number} min
   * @param {number | null} max
   * @param {number} step
   */
  #snapToQuantityRule(value, min, max, step) {
    let qty = Math.max(min, value);

    if (step > 1) {
      const remainder = (qty - min) % step;
      if (remainder !== 0) {
        qty += step - remainder;
      }
    }

    if (max !== null) {
      qty = Math.min(max, qty);
    }

    return qty;
  }

  #getQuantitySelector() {
    const productForm = this.closest('product-form-component');
    return productForm?.querySelector('quantity-selector-component') ?? null;
  }

  /**
   * @param {number} units
   */
  #syncQuantity(units) {
    const quantitySelector = this.#getQuantitySelector();
    if (!quantitySelector?.setValue) return;

    const { min, max, step } = quantitySelector.getCurrentValues();
    const qty = this.#snapToQuantityRule(units, min, max, step);

    quantitySelector.setValue(String(qty));
    quantitySelector.onQuantityChange?.();
    quantitySelector.updateButtonStates?.();
  }

  /**
   * @param {number} units
   */
  #applyUnits(units) {
    const quantitySelector = this.#getQuantitySelector();
    let qty = units;

    if (quantitySelector?.getCurrentValues) {
      const { min, max, step } = quantitySelector.getCurrentValues();
      qty = this.#snapToQuantityRule(units, min, max, step);
    }

    this.refs.unitsValue.value = String(qty);
    this.#updatePricing(qty);
    this.#syncCoverageProperties();
    this.#syncQuantity(qty);
  }

  /**
   * Persist per-unit coverage as line item properties so cart can
   * recompute coverage when quantity changes after add-to-cart.
   */
  #syncCoverageProperties() {
    const { sqmPerUnit } = this.#getConfig();
    const eachInput = this.querySelector('[data-coverage-each-input]');
    const unitInput = this.querySelector('[data-coverage-unit-input]');

    if (!(eachInput instanceof HTMLInputElement) || !(unitInput instanceof HTMLInputElement)) return;
    if (!Number.isFinite(sqmPerUnit) || sqmPerUnit <= 0) return;

    eachInput.value = String(Math.round(sqmPerUnit * 10000) / 10000);
    unitInput.value = 'm2';
  }

  /**
   * @param {number} units
   */
  #updatePricing(units) {
    const { variantPrice } = this.#getConfig();
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'AUD';
    const totalCents = variantPrice * units;

    this.refs.unitPrice.textContent = this.#formatUnitPrice(variantPrice, moneyFormat, currency);
    this.refs.totalValue.textContent = formatMoney(totalCents, moneyFormat, currency);
  }

  /**
   * @param {number} variantPrice
   * @param {string} moneyFormat
   * @param {string} currency
   */
  #formatUnitPrice(variantPrice, moneyFormat, currency) {
    const displayUnit = (this.dataset.displayUnit || '').toLowerCase();
    const formatted = formatMoney(variantPrice, moneyFormat, currency);
    const perBox = this.dataset.perBoxLabel || '/ box';
    const perPiece = this.dataset.perPieceLabel || '/ piece';

    if (displayUnit === 'pack' || displayUnit === 'box') {
      return `${formatted} ${perBox}`;
    }

    return `${formatted} ${perPiece}`;
  }

  #recalculate() {
    const { sqmPerUnit, variantPrice, sequenceGroup, displayUnit, salesUnit } = this.#getConfig();
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'AUD';

    const width = parseFloat(this.refs.widthInput.value) || 0;
    const length = parseFloat(this.refs.lengthInput.value) || 0;
    const hasDimensions = width > 0 && length > 0;

    this.dataset.active = hasDimensions ? 'true' : 'false';
    this.#updateWastageDisplay();

    if (!hasDimensions) {
      this.refs.areaSummary.hidden = true;
      const defaultQty = this.#getQuantitySelector()?.getCurrentValues?.().min ?? 1;
      this.refs.unitsValue.value = String(defaultQty);
      this.#updatePricing(defaultQty);
      return;
    }

    const widthMetres = this.#measureUnit === 'cm' ? width / 100 : width;
    const lengthMetres = this.#measureUnit === 'cm' ? length / 100 : length;
    const areaSqm = widthMetres * lengthMetres;
    const areaWithWastage = areaSqm * (1 + this.#wastage / 100);
    const rawUnits = areaWithWastage / sqmPerUnit;

    let units;
    if (this.#manualUnits && this.#manualUnitsValue !== null) {
      units = this.#manualUnitsValue;
    } else {
      units = this.#roundUnits(rawUnits, sequenceGroup, displayUnit, salesUnit);
    }

    const sqmPriceCents = sqmPerUnit > 0 ? Math.round(variantPrice / sqmPerUnit) : 0;
    const formattedMeasuredArea = areaSqm.toFixed(2);
    const formattedWastageArea = areaWithWastage.toFixed(2);
    const formattedSqmPrice = formatMoney(sqmPriceCents, moneyFormat, currency);

    this.refs.measuredAreaLine.textContent = `Measured area: ${formattedMeasuredArea}m²`;
    this.refs.wastageAreaLine.textContent = `Area including ${this.#wastage}% wastage: ${formattedWastageArea}m²`;
    this.refs.areaPriceLine.textContent = `${formattedSqmPrice} / m²`;
    this.refs.areaSummary.hidden = false;

    this.#applyUnits(units);
  }
}

if (!customElements.get('tile-calculator-component')) {
  customElements.define('tile-calculator-component', TileCalculatorComponent);
}
